import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import styles from "./CalendarDemo.module.css";

const US_TIMEZONES = [
  { label: "Eastern (EST)", value: "America/New_York" },
  { label: "Central (CST)", value: "America/Chicago" },
  { label: "Mountain (MST)", value: "America/Denver" },
  { label: "Pacific (PST)", value: "America/Los_Angeles" },
  { label: "Alaska (AKST)", value: "America/Anchorage" },
  { label: "Hawaii (HST)", value: "Pacific/Honolulu" },
];

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_NAMES_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const PROVIDER_TZ = "America/Chicago";
const PROVIDER_START_HOUR = 8;
const PROVIDER_END_HOUR = 20;
const SLOT_INTERVAL_MIN = 30;

const getTimezoneLabel = (tz) => US_TIMEZONES.find((t) => t.value === tz)?.label ?? tz;

const isWeekday = (date) => {
  const day = date.getDay();
  return day !== 0 && day !== 6;
};

const getNext7Weekdays = () => {
  const days = [];
  const cursor = new Date();
  cursor.setDate(cursor.getDate() + 1);
  cursor.setHours(0, 0, 0, 0);
  while (days.length < 7) {
    if (isWeekday(cursor)) days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
};

const formatDayButton = (date) => ({
  dayName: DAY_NAMES[date.getDay()],
  dateLabel: `${MONTH_NAMES_SHORT[date.getMonth()]} ${date.getDate()}`,
  key: date.toISOString().slice(0, 10),
  date,
});

const formatDateForApi = (dateKey) => {
  const d = new Date(dateKey + "T12:00:00");
  return `${MONTH_NAMES_FULL[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

// Converts a date+time in a given timezone to a UTC ISO string.
// Uses double-offset estimation via Intl.DateTimeFormat:
// 1. Create a UTC timestamp as if the local time were UTC
// 2. Use Intl to find what that UTC instant looks like in the target timezone
// 3. Compute the offset and adjust
// Note: Can be off by ~1 hour during DST transitions (spring-forward/fall-back)
// because the initial UTC guess may land in the wrong DST period. For appointment
// scheduling with 15-min slots, this is acceptable — the server validates the final time.
const toUtcIso = (dateKey, timeValue, tz) => {
  const [year, month, day] = dateKey.split("-").map(Number);
  const [hour, minute] = timeValue.split(":").map(Number);
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(new Date(utcGuess));
  const get = (type) => parts.find((p) => p.type === type)?.value;
  const tzAsUtc = Date.UTC(+get("year"), +get("month") - 1, +get("day"), +get("hour"), +get("minute"), 0);
  return new Date(utcGuess - (tzAsUtc - utcGuess)).toISOString();
};

// Generate time slots for a given date, converting provider hours (Central/CST) to user's timezone.
// Provider availability: 8 AM - 8 PM Central. Slots display in user's local timezone.
// Each slot includes providerTime (HH:MM in Central) for matching against API availability data.
const generateTimeSlots = (dateKey, userTz) => {
  const slots = [];
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: userTz,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  for (let hour = PROVIDER_START_HOUR; hour <= PROVIDER_END_HOUR; hour++) {
    for (let min = 0; min < 60; min += SLOT_INTERVAL_MIN) {
      if (hour === PROVIDER_END_HOUR && min > 0) break;
      const providerTime = `${String(hour).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
      const utcIso = toUtcIso(dateKey, providerTime, PROVIDER_TZ);
      const label = formatter.format(new Date(utcIso));
      slots.push({ label, value: utcIso, providerTime });
    }
  }
  return slots;
};

// Card 6826 — REFILL MODE.
//
// This page owns the purchase scheduler. A DrCare refill needs the identical picker, so it is
// reused rather than cloned; the only difference is where "Confirm" lands. Refill mode is carried
// in ROUTE STATE and nowhere else — one source of truth, and React Router keeps it in
// `history.state`, so a refresh stays in refill mode instead of silently falling back to the
// purchase flow and sending the user to /checkout with someone's refill in hand.
//
// Absent state = the purchase flow, byte-for-byte as before.
const readRefillOrder = (locationState) => locationState?.refillOrder ?? null;

// Card 40756 — SCHEDULING IS CONDITIONAL, NOT THE DEFAULT NEXT STEP.
//
// Since card 40751 a DrCare intake routes straight to `/checkout` and a licensed physician reviews
// it asynchronously; `shouldOfferScheduling` (web/utils/asyncConsultCopy.js) is the predicate that
// decides who may still be offered a booking. Only two kinds of request reach THIS page now: a
// product that genuinely requires a live visit, and a refill whose `calculateRefill` returned
// `schedulingRequired`.
//
// The old copy — "Schedule Your Telehealth Appointment" over "Select a date and time below" — read
// as the normal next step after any order, which is exactly what trained members to expect a call
// every time. The wording below names THIS request as the reason, and the note says plainly that
// the appointment is the exception.
const SCHEDULING_TITLE = "This Request Needs a Telehealth Appointment";

const SCHEDULING_EXCEPTION_NOTE =
  "Most requests do not need an appointment — a licensed physician reviews them online and sends an approval or denial. This one is an exception.";

// Pure and total: a null order, or one whose `orderNumber` never loaded, still yields a sentence
// that reads correctly rather than "order  includes".
const deriveSchedulingReason = (refillOrder) => {
  if (!refillOrder) {
    return "This request includes a live visit with a licensed physician, so a time is required before it can be approved.";
  }
  const orderNumber = refillOrder.orderNumber ?? "";
  return orderNumber
    ? `Your refill of order ${orderNumber} includes a telehealth visit, so a time is required before it can be filled.`
    : "This refill includes a telehealth visit, so a time is required before it can be filled.";
};

const CalendarDemo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const refillOrder = readRefillOrder(location.state);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const weekdays = useMemo(() => getNext7Weekdays().map(formatDayButton), [new Date().toDateString()]);
  const [selectedDayKey, setSelectedDayKey] = useState(weekdays[0]?.key ?? "");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [timezone, setTimezone] = useState("America/New_York");
  const [tzConfirmMsg, setTzConfirmMsg] = useState("");
  const [detectedNonUsTz, setDetectedNonUsTz] = useState(null);
  const [availableSet, setAvailableSet] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(true);

  const timeSlots = useMemo(
    () => selectedDayKey ? generateTimeSlots(selectedDayKey, timezone) : [],
    [selectedDayKey, timezone]
  );

  useEffect(() => {
    try {
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const isUs = US_TIMEZONES.some((t) => t.value === detected);
      if (isUs) {
        setTimezone(detected);
      } else if (detected) {
        setDetectedNonUsTz(detected);
      }
    } catch { /* keep default */ }
  }, []);

  // Fetch available slots from API for the full date range
  useEffect(() => {
    if (weekdays.length === 0) return;
    const startDate = weekdays[0].key;
    const endDate = weekdays[weekdays.length - 1].key;
    setSlotsLoading(true);
    fetch(`/api/calendar/available-slots?startDate=${startDate}&endDate=${endDate}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const set = new Set();
        (data.slots ?? []).forEach((day) => {
          (day.times ?? []).forEach((time) => set.add(`${day.date}:${time}`));
        });
        setAvailableSet(set);
      })
      .catch((err) => {
        console.warn("Failed to fetch available slots, showing all:", err);
        setAvailableSet(null);
      })
      .finally(() => setSlotsLoading(false));
  }, [weekdays]);

  useEffect(() => {
    if (!tzConfirmMsg) return;
    const timer = setTimeout(() => setTzConfirmMsg(""), 2500);
    return () => clearTimeout(timer);
  }, [tzConfirmMsg]);

  const handleDaySelect = useCallback((key) => {
    setSelectedDayKey(key);
    setSelectedTimeSlot(null);
  }, []);

  const handleTimezoneChange = useCallback((e) => {
    const newTz = e.target.value;
    setTimezone(newTz);
    setSelectedTimeSlot(null);
    const label = US_TIMEZONES.find((t) => t.value === newTz)?.label ?? newTz;
    setTzConfirmMsg(`Timezone changed to ${label}`);
  }, []);

  const canConfirm = selectedDayKey && selectedTimeSlot;

  const handleConfirm = useCallback(() => {
    if (!canConfirm) return;

    const selectedSlot = timeSlots.find((s) => s.value === selectedTimeSlot);
    const timeLabel = selectedSlot?.label ?? "";
    const dateForApi = formatDateForApi(selectedDayKey);
    const utcTime = selectedTimeSlot;

    const appointmentTime = {
      date: dateForApi,
      time: timeLabel,
      timezone: getTimezoneLabel(timezone),
      utcTime,
    };

    // Refill mode returns to Orders carrying the order it started from, so the checkout modal
    // reopens on the SAME order with the slot attached. The order travels as an object rather
    // than an id because /orders paginates — an id would need a lookup that can miss.
    if (refillOrder) {
      navigate("/orders", {
        state: { refillOrder, refillAppointment: appointmentTime },
        replace: true,
      });
      return;
    }

    const checkoutParams = new URLSearchParams();
    for (const [key, value] of searchParams.entries()) {
      checkoutParams.set(key, value);
    }
    const qs = checkoutParams.toString();
    const checkoutUrl = `/checkout${qs ? `?${qs}` : ""}`;

    navigate(checkoutUrl, {
      state: { appointmentTime },
    });
  }, [canConfirm, selectedDayKey, selectedTimeSlot, timezone, timeSlots, searchParams, navigate, refillOrder]);

  // Back from refill mode: return the order WITHOUT a slot. Orders reopens the dosage-decision
  // step, so the user keeps their place and cannot reach a Pay button that would 400 for want of
  // an appointment.
  const handleRefillBack = useCallback(() => {
    navigate("/orders", { state: { refillOrder }, replace: true });
  }, [navigate, refillOrder]);

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.header}>
          {/* Refill mode only. Sited in the header, ⛔ NOT in .confirmSection: at <=768px that
              section becomes a FIXED bottom bar whose measured 80px height is what .page's
              96px bottom padding is derived from. A second button there wraps at 390px, the bar
              grows past 96px, and the last time slot is covered — the exact load-bearing-padding
              defect recorded for this stylesheet. */}
          {refillOrder && (
            <button
              type="button"
              className={styles.backBtn}
              onClick={handleRefillBack}
            >
              Back to Orders
            </button>
          )}
          <h1 className={styles.title}>{SCHEDULING_TITLE}</h1>
          <p className={styles.subtitle}>{deriveSchedulingReason(refillOrder)}</p>
          <p className={styles.requirementNote}>{SCHEDULING_EXCEPTION_NOTE}</p>
        </div>

        <div className={styles.tzRow}>
          <label htmlFor="tz-select" className={styles.tzLabel}>Timezone:</label>
          <select
            id="tz-select"
            className={styles.timezoneSelect}
            value={timezone}
            onChange={handleTimezoneChange}
          >
            {US_TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
          {tzConfirmMsg && <span className={styles.tzConfirm}>{tzConfirmMsg}</span>}
        </div>

        {detectedNonUsTz && (
          <div className={`${styles.statusMessage} ${styles.warningMessage}`}>
            Your detected timezone ({detectedNonUsTz}) is outside the US. Times are displayed in {getTimezoneLabel(timezone)}. You can change this above.
          </div>
        )}

        <div className={styles.dayBar}>
          {weekdays.map((wd) => (
            <button
              key={wd.key}
              type="button"
              className={`${styles.dayBtn} ${selectedDayKey === wd.key ? styles.dayBtnSelected : ""}`}
              onClick={() => handleDaySelect(wd.key)}
              aria-label={`${wd.dayName} ${wd.dateLabel}`}
              aria-pressed={selectedDayKey === wd.key}
            >
              <span className={styles.dayBtnName}>{wd.dayName}</span>
              <span className={styles.dayBtnDate}>{wd.dateLabel}</span>
            </button>
          ))}
        </div>

        {selectedDayKey && slotsLoading && (
          <div className={styles.loadingSlots}>Loading available times...</div>
        )}

        {selectedDayKey && !slotsLoading && (
          <div className={styles.timeGrid}>
            {timeSlots.map((slot) => {
              const isPast = new Date(slot.value).getTime() < Date.now();
              const isBooked = !isPast && availableSet !== null && !availableSet.has(`${selectedDayKey}:${slot.providerTime}`);
              const isDisabled = isPast || isBooked;
              return (
                <button
                  key={slot.value}
                  type="button"
                  className={`${styles.timeSlot} ${selectedTimeSlot === slot.value ? styles.timeSlotSelected : ""} ${isPast ? styles.timeSlotPast : ""} ${isBooked ? styles.timeSlotBooked : ""}`}
                  onClick={() => { if (!isDisabled) setSelectedTimeSlot(slot.value); }}
                  aria-label={`${slot.label}${isBooked ? " (unavailable)" : ""}`}
                  aria-pressed={selectedTimeSlot === slot.value}
                  aria-disabled={isDisabled}
                >
                  {slot.label}
                </button>
              );
            })}
          </div>
        )}

        <div className={styles.confirmSection}>
          <button
            type="button"
            className={styles.confirmBtn}
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            Confirm Appointment
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalendarDemo;
